/**
 * alatunniste-yhteydenotto.js — the footer contact form's two small jobs.
 *
 * Added 2026-09-23 with sections/alatunniste-yhteydenotto.liquid; the reasoning
 * for both is in that file's header.
 *
 * 1. SPAM FILTERING, at submit, before Shopify's native post. Superseded the
 *    same day: this used to be "honeypot only — no timer and no scoring,
 *    because a real message silently dropped is the one outcome this must
 *    never produce". That principle still stands; what changed is how it is
 *    met. The rule now is: NOTHING IS EVER SILENTLY DROPPED EXCEPT A FILLED
 *    HONEYPOT.
 *
 *    a. Honeypot filled (`susanna_lisatieto`) → the post is cancelled, the
 *       form is reset, and it shows the same success line a person sees. A bot
 *       learns nothing. No person fills a field they cannot see or reach.
 *    b. Otherwise the message is scored by assets/alatunniste-roskaposti.js
 *       (heidisimelius.fi's spamCheck.ts, ported): name, email, message, phone
 *       and the time since this script started (restarted on every reset).
 *       - "spam" in the BOT'S EXACT SHAPE (name AND message both gibberish —
 *         `botShape`) → the post is cancelled, but the form does NOT pretend
 *         it worked and does NOT clear what was typed: it shows the editable
 *         "roskaposti_viesti" line with the direct email address, so a real
 *         person who was misclassified still has a way through. Only when
 *         that address is actually rendered; otherwise the post goes, tagged;
 *       - any other "spam" → SENT, tagged "Todennäköinen roskaposti" (changed
 *         2026-09-23 after review: a score of 6 is reachable by real people);
 *       - "suspect" → the post goes through, carrying a hidden
 *         `contact[Roskapostiarvio]` field. Shopify prints every contact[…]
 *         field in the notification email, so Susanna sees the tag and the
 *         reasons above the message;
 *       - "ham" → the post goes through as it is.
 *    Heidi's server REROUTES spam to a tech inbox. Nothing like that is
 *    possible here: there is no server of ours between this form and Shopify,
 *    and the recipient is a store setting. Hence the direct-email line.
 *
 *    Fails open, always: if the scorer did not load or throws, the form
 *    behaves as it did before (honeypot only) and posts. A missing script must
 *    never cost a real message. And a bot that POSTs to /contact directly
 *    never runs any of this — that residual risk is recorded in
 *    docs/open-work.md, not solved here.
 *
 * 2. THE "OTA YHTEYTTÄ" LINKS. The footer link is `/#ota-yhteytta` — a
 *    Shopify `url` setting cannot hold a bare fragment — which on any page
 *    other than the front page would navigate away. This form is on EVERY
 *    page, so a click on any link ending in the form's anchor stays put and
 *    brings the form into view instead. Without the script the link still
 *    works; it just goes to the front page's copy of the same form.
 *
 * Works with the script blocked: the form posts natively to Shopify.
 */
(() => {
  const form = document.getElementById('AlatunnisteYhteydenotto');
  if (form == null) return;

  const section = form.closest('section');
  const trap = form.querySelector('[data-yhteydenotto-ansa]');
  const status = form.querySelector('[data-yhteydenotto-tila]');
  const success = form.querySelector('template[data-yhteydenotto-onnistui]');
  const blocked = form.querySelector('template[data-yhteydenotto-roskaposti]');

  /* The time trap: started when the form becomes interactive (this deferred
     script runs once the document is parsed), restarted on every reset.

     ⚠️ SWITCHED OFF when the form already holds text as this script starts:
     Shopify's error page re-renders the fields with what was typed, and a
     browser restores them after a reload — in both cases a real person can
     press send within the 3-second window. `timerOn` comes back on after a
     reset (an empty form typed from scratch). Review finding, 2026-09-23. */
  const prefilled = ['contact[name]', 'contact[email]', 'contact[body]'].some((name) => {
    const field = form.elements.namedItem(name);
    return field != null && typeof field.value === 'string' && field.value.trim() !== '';
  });
  let timerOn = !prefilled;
  let startedAt = Date.now();
  form.addEventListener('reset', () => {
    startedAt = Date.now();
    timerOn = true;
  });

  const showStatus = (template) => {
    if (status == null || template == null) return;
    status.replaceChildren(template.content.cloneNode(true));
    status.querySelector('[tabindex]')?.focus();
  };

  const fieldValue = (name) => {
    const field = form.elements.namedItem(name);
    return field != null && typeof field.value === 'string' ? field.value : undefined;
  };

  /* The hidden tag field is created only when a message is "suspect", and
     removed otherwise, so an ordinary email carries no extra line. */
  const TAG_NAME = 'contact[Roskapostiarvio]';
  const setTag = (text) => {
    let tag = form.querySelector(`input[type="hidden"][name="${TAG_NAME}"]`);
    if (text == null) {
      tag?.remove();
      return;
    }
    if (tag == null) {
      tag = document.createElement('input');
      tag.type = 'hidden';
      tag.name = TAG_NAME;
      form.append(tag);
    }
    tag.value = text;
  };

  /* Returns the assessment, or null when the scorer is missing or fails — in
     which case the caller lets the post go (fail open). */
  const assess = () => {
    const scorer = globalThis.susannaRoskaposti;
    if (typeof scorer?.assessSpam !== 'function') return null;
    try {
      return scorer.assessSpam({
        name: fieldValue('contact[name]') ?? '',
        email: fieldValue('contact[email]') ?? '',
        message: fieldValue('contact[body]') ?? '',
        phone: fieldValue('contact[phone]'),
        website: trap?.value ?? '',
        elapsedMs: timerOn ? Date.now() - startedAt : undefined,
      });
    } catch {
      return null;
    }
  };

  /* ⚠️ preventDefault() ALONE DOES NOT STOP THE POST — found on live
     2026-09-23 with a filled honeypot that was still sent. Shopify injects an
     inline script into every storefront page that listens for `submit` on the
     DOCUMENT (bubble phase). It does not check `defaultPrevented`: it adds a
     hidden `form_key`, saves the form in browser storage and calls
     `form.submit()` itself, which fires no event and so cannot be cancelled
     afterwards. Our listener is on the form, so it runs first; stopping the
     event there keeps it from ever bubbling to Shopify's. Only the two BLOCK
     paths use this. A message that is sent (plain or tagged) keeps bubbling
     and goes out through Shopify's own path, form_key and all. */
  const stopPost = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  form.addEventListener('submit', (event) => {
    // a. The honeypot: the one signal strong enough to act on alone.
    if (trap != null && trap.value.trim() !== '') {
      stopPost(event);
      setTag(null);
      form.reset();
      showStatus(success);
      return;
    }

    // b. The scorer. No assessment → post exactly as before.
    const result = assess();
    if (result == null || result.verdict === 'ham') {
      setTag(null);
      return;
    }

    /* BLOCK ONLY THE BOT'S EXACT SHAPE — name AND message both machine-made
       (`botShape`, see the scorer). Heidi's threshold of 6 decided a ROUTE on
       her site (spam went to a tech inbox); here the only alternative to
       sending is blocking, and a 6 is reachable by real people. So every
       other score is SENT, tagged, and Susanna's mail filter can sort it.
       Review finding, 2026-09-23.

       And cancel only when the person can be told why AND where to write
       instead: the message must be there and must carry the mailto link
       (it is left out while the store email is unset). The node is built
       BEFORE the post is cancelled, so a failure here can only mean "sent". */
    if (result.verdict === 'spam' && result.botShape === true && status != null && blocked != null) {
      const message = blocked.content.cloneNode(true);
      if (message.querySelector('a[href^="mailto:"]') != null) {
        stopPost(event);
        setTag(null);
        status.replaceChildren(message);
        status.querySelector('[tabindex]')?.focus();
        return;
      }
    }

    const label = result.verdict === 'spam' ? 'Todennäköinen roskaposti' : 'Mahdollinen roskaposti';
    setTag(`${label} (pisteet ${result.score}): ${result.reasons.join('; ')}`);
  });

  if (section?.id == null || section.id === '') return;
  const anchor = `#${section.id}`;
  const reduceMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)');

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link == null) return;

    const href = link.getAttribute('href') ?? '';
    if (!href.endsWith(anchor)) return;

    const path = href.slice(0, -anchor.length);
    if (path !== '' && path !== '/' && path !== globalThis.location.pathname) return;

    event.preventDefault();
    const behavior = reduceMotion.matches ? 'auto' : 'smooth';

    /* While the footer reveal is engaged (assets/alatunniste.js) the whole
       footer is position:fixed BEHIND the page, so scrollIntoView has nothing
       to scroll to. The form comes into view by scrolling the page to its end
       instead. Above 990px Horizon scrolls .page-wrapper, not the document. */
    if (document.body.classList.contains('alatunniste-paljastus')) {
      const wrapper = document.querySelector('.page-wrapper');
      const scroller =
        wrapper != null && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : document.scrollingElement;
      scroller?.scrollTo({ top: scroller.scrollHeight, behavior });
    } else {
      section.scrollIntoView({ behavior, block: 'start' });
    }
    form.querySelector('input:not([type="hidden"], [tabindex="-1"]), textarea')?.focus({ preventScroll: true });
    history.replaceState(null, '', anchor);
  });
})();
